import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  HandCoins, Users, TrendingUp, Euro, Gift, Copy, ExternalLink,
  CheckCircle, Clock, Sparkles, Plus, Search, Filter,
  Share2, Mail, Building2, Phone, Calendar, ArrowRight,
  Target, Award, Briefcase
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';

const AffiliateDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNewGain, setShowNewGain] = useState(false);

  // Affiliate stats
  const stats = user?.affiliate_stats || {
    total_referrals: 0,
    converted_referrals: 0,
    pending_commission: 0,
    total_earned: 0,
    conversion_rate: 0,
    last_earning: null
  };

  // Calculate conversion rate
  const conversionRate = stats.total_referrals > 0 
    ? Math.round((stats.converted_referrals / stats.total_referrals) * 100) 
    : 0;

  // Referral link
  const referralLink = `${window.location.origin}/site?ref=${user?.id || 'demo'}`;

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Lien de parrainage copié !');
  };

  // Demo: Trigger gain animation
  useEffect(() => {
    if (stats.last_earning && stats.total_earned > 0) {
      const timer = setTimeout(() => {
        setShowNewGain(true);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#22c55e', '#16a34a', '#10b981', '#4ade80']
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Demo leads data
  const recentLeads = [
    { id: 1, company: 'TechStart SAS', contact: 'Marie Dupont', status: 'qualified', date: '2024-12-20', potential: '5 000€' },
    { id: 2, company: 'Design Studio', contact: 'Pierre Martin', status: 'contacted', date: '2024-12-18', potential: '3 500€' },
    { id: 3, company: 'WebAgency Pro', contact: 'Sophie Leroy', status: 'converted', date: '2024-12-15', potential: '8 000€' },
  ];

  const getStatusBadge = (status) => {
    const config = {
      'new': { color: 'bg-blue-100 text-blue-700', label: 'Nouveau' },
      'contacted': { color: 'bg-amber-100 text-amber-700', label: 'Contacté' },
      'qualified': { color: 'bg-purple-100 text-purple-700', label: 'Qualifié' },
      'converted': { color: 'bg-emerald-100 text-emerald-700', label: 'Converti' },
      'lost': { color: 'bg-slate-100 text-slate-700', label: 'Perdu' },
    };
    return config[status] || config['new'];
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Bonjour {user?.first_name} ! 👋
              </h1>
              <p className="text-slate-500 mt-1">
                Espace Apporteur d'Affaires • Gérez vos leads et commissions
              </p>
            </div>
            <Button 
              onClick={copyReferralLink}
              className="hidden md:flex gap-2"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Share2 className="w-4 h-4" />
              Copier mon lien
            </Button>
          </div>
        </div>

        {/* New Gain Animation */}
        {showNewGain && stats.last_earning && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white animate-pulse">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              <div>
                <p className="font-bold text-lg">Nouveau gain ! 🎉</p>
                <p className="text-emerald-100">
                  +{stats.last_earning.amount}€ grâce à {stats.last_earning.client}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Euro className="w-5 h-5 text-emerald-600" />
                </div>
                <Badge className="bg-emerald-100 text-emerald-700">Total</Badge>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.total_earned.toLocaleString('fr-FR')}€
              </div>
              <p className="text-sm text-slate-500">Gains totaux</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.pending_commission.toLocaleString('fr-FR')}€
              </div>
              <p className="text-sm text-slate-500">Commissions à venir</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.total_referrals}
              </div>
              <p className="text-sm text-slate-500">Leads générés</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {conversionRate}%
              </div>
              <p className="text-sm text-slate-500">Taux de conversion</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Leads */}
          <Card className="lg:col-span-2 border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Mes derniers leads</CardTitle>
                <CardDescription>Suivez vos opportunités en cours</CardDescription>
              </div>
              <Link to="/affiliate/leads">
                <Button variant="outline" size="sm">
                  Voir tout
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentLeads.map((lead) => {
                  const statusConfig = getStatusBadge(lead.status);
                  return (
                    <div 
                      key={lead.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: theme.colors?.primary }}>
                            {lead.company}
                          </p>
                          <p className="text-sm text-slate-500">{lead.contact}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium text-emerald-600">{lead.potential}</p>
                          <p className="text-xs text-slate-400">{lead.date}</p>
                        </div>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Actions & Referral */}
          <div className="space-y-6">
            {/* Referral Link Card */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-green-600 text-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="w-5 h-5" />
                  <span className="font-semibold">Lien de parrainage</span>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-lg p-3 mb-3">
                  <p className="text-sm truncate font-mono">{referralLink}</p>
                </div>
                <Button 
                  onClick={copyReferralLink}
                  className="w-full bg-white text-emerald-600 hover:bg-emerald-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le lien
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/affiliate/leads" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Plus className="w-4 h-4" />
                    Ajouter un lead
                  </Button>
                </Link>
                <Link to="/affiliate/commissions" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <HandCoins className="w-4 h-4" />
                    Voir mes commissions
                  </Button>
                </Link>
                <Link to="/affiliate/contract" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Award className="w-4 h-4" />
                    Mon contrat
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Commission Info */}
            <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                      Commission : 10% du premier projet
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Payée dès validation du paiement client
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AffiliateDashboard;
